/*
 * OEM-Datenpunkte eines Geräts in der Datenpunkte-Ansicht
 * (docs/servicezugang-oem-und-fern.md). Statt des Betriebsschemas alle
 * Punkte, die der Regler kennt: bei BSB der Katalog, sonst das OEM-Schema
 * aus Erkundung oder Registertabelle. Werte kommen über den Fernzugang,
 * nicht über die Telemetrie.
 *
 * Der Bus am Regler schafft gut hundert Lesungen in der Minute. Deshalb
 * (Patrick, 22.10.2026):
 *   - es ist immer nur eine Gruppe offen, und nur die wird gelesen;
 *   - bei einem Filter werden höchstens die ersten 100 Treffer gezeigt und
 *     gelesen;
 *   - gelesen wird ein Punkt nach dem anderen; Zuklappen oder ein neuer
 *     Filter hält das Lesen an.
 * Zeilen sehen aus wie die des Betriebsschemas; jede zeigt das Alter ihres
 * Werts.
 */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { StatusBadge } from './ui/StatusBadge';
import { ServiceEditModal } from './ServiceEditModal';
import { errorText, findServiceClient, serviceCall } from './serviceRemoteApi';
import {
  ageOf, commonGroupPrefix, displayValue, findPoints, formatAge, levelName, rawValue, stripGroup,
  type ServicePointView, type ServiceReading,
} from './serviceRemoteModel';

/** So viele Treffer zeigt und liest der Filter höchstens. */
export const OEM_FILTER_LIMIT = 100;

interface Group { gruppe: string; anzahl: number }

interface Props {
  plantId: string;
  deviceId: string;
  /** Filter der Seite, schon klein geschrieben. */
  filter: string;
  /** Lesefortschritt nach aussen, damit der klebende Geraetekopf ihn zeigt:
   *  in einer langen Gruppe scrollt die Zeile hier sonst aus dem Blick. */
  onProgress?: (progress: { done: number; total: number } | null, stop: () => void) => void;
}

export function OemDeviceTree({ plantId, deviceId, filter, onProgress }: Props) {
  const [client, setClient] = useState<{ clientId: string; level: string } | null>(null);
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [points, setPoints] = useState<ServicePointView[] | null>(null);
  const [catalog, setCatalog] = useState<ServicePointView[] | null>(null);
  const [readings, setReadings] = useState<Map<string, ServiceReading>>(new Map());
  const [reading, setReading] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ServicePointView | null>(null);
  const [now, setNow] = useState(Date.now());
  const [attempt, setAttempt] = useState(0);
  const readRun = useRef(0);
  const query = filter.trim();

  const call = <T,>(path: string, body: unknown, wait = 45) =>
    serviceCall<T>(plantId, client!.clientId, path, body, wait);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 5000);
    return () => { window.clearInterval(id); readRun.current++; };
  }, []);

  // Field-Client finden, dann die Gruppen.
  useEffect(() => {
    let live = true;
    setError(null);
    setClient(null);
    setGroups(null);
    findServiceClient(plantId, deviceId)
      .then(async (c) => {
        if (!live) return;
        setClient(c);
        const a = await serviceCall<Group[]>(plantId, c.clientId, `geraete/${deviceId}/gruppen`, {});
        if (live) setGroups(Array.isArray(a.body) ? a.body : []);
      })
      .catch((e) => { if (live) setError(errorText(e)); });
    return () => { live = false; };
  }, [plantId, deviceId, attempt]);

  const lastOf = (v: ServicePointView) => readings.get(v.point.address) ?? v.last ?? null;

  /** Nacheinander lesen; jede neue Auswahl hält einen laufenden Durchgang an. */
  const readAll = async (list: readonly ServicePointView[]) => {
    const token = ++readRun.current;
    setProgress({ done: 0, total: list.length });
    let failed = 0;
    for (let i = 0; i < list.length; i++) {
      if (readRun.current !== token) return;
      const address = list[i]!.point.address;
      setReading(address);
      try {
        const a = await call<ServiceReading>(`geraete/${deviceId}/lesen`, { adresse: address });
        if (readRun.current !== token) return;
        setReadings((m) => new Map(m).set(address, a.body));
      } catch {
        failed++;
      }
      if (readRun.current !== token) return;
      setProgress({ done: i + 1, total: list.length });
    }
    setReading(null);
    setProgress(null);
    if (failed > 0) setError(`${failed} von ${list.length} Punkten nicht gelesen.`);
  };

  const stop = () => { readRun.current++; setReading(null); setProgress(null); };

  const stopRef = useRef(stop);
  stopRef.current = stop;
  useEffect(() => { onProgress?.(progress, () => stopRef.current()); }, [progress]);
  useEffect(() => () => onProgress?.(null, () => {}), []);

  // Offene Gruppe: ihre Punkte holen und lesen. Zu oder andere Gruppe: anhalten.
  useEffect(() => {
    stop();
    setPoints(null);
    if (!client || open === null || query) return;
    let live = true;
    call<ServicePointView[]>(`geraete/${deviceId}/punkte`, { gruppe: open })
      .then((a) => {
        if (!live) return;
        const list = Array.isArray(a.body) ? a.body : [];
        setPoints(list);
        void readAll(list);
      })
      .catch((e) => { if (live) setError(errorText(e)); });
    return () => { live = false; };
  }, [client, open, query === '']);

  // Filter: ganzer Katalog einmal, dann die ersten 100 Treffer lesen, wenn
  // die Eingabe kurz ruht.
  useEffect(() => {
    if (!client || !query || catalog !== null) return;
    let live = true;
    call<ServicePointView[]>(`geraete/${deviceId}/punkte`, {}, 75)
      .then((a) => { if (live) setCatalog(Array.isArray(a.body) ? a.body : []); })
      .catch((e) => { if (live) setError(errorText(e)); });
    return () => { live = false; };
  }, [client, query !== '', catalog === null]);

  const hits = useMemo(() => (catalog && query ? findPoints(catalog, query, OEM_FILTER_LIMIT) : []), [catalog, query]);
  const totalHits = useMemo(() => (catalog && query ? findPoints(catalog, query, Number.MAX_SAFE_INTEGER).length : 0), [catalog, query]);

  useEffect(() => {
    stop();
    if (!query || hits.length === 0) return;
    const id = window.setTimeout(() => void readAll(hits), 600);
    return () => window.clearTimeout(id);
  }, [hits]);

  const prefix = commonGroupPrefix((groups ?? []).map((g) => g.gruppe));
  const toggle = (g: string) => setOpen((o) => (o === g ? null : g));

  const row = (v: ServicePointView, depth: number, showGroup: boolean) => (
    <OemLeafRow key={v.point.address} view={v} last={lastOf(v)} depth={depth} now={now}
                reading={reading === v.point.address} group={showGroup ? stripGroup(v.point.group, prefix) : null}
                ownLevel={client?.level ?? null} onEdit={() => setEditing(v)} />
  );

  return (
    <div class="stack-2 hv-oem">
      <div class="hv-oem__status" role="status">
        <StatusBadge tone="warn">OEM-Modus</StatusBadge>
        <span class="muted">
          {client ? `Alle Punkte des Reglers, Ebene ${levelName(client.level)}` : 'Suche den Field-Client…'}
          {prefix ? ` · ${prefix.slice(0, -2)}` : ''}
          {' · Werte über den Fernzugang, nur was offen ist.'}
        </span>
        {progress && !onProgress && (
          <>
            <span class="muted">Lese {progress.done} von {progress.total}…</span>
            <button type="button" class="hv-btn hv-btn--sm" onClick={stop}>Anhalten</button>
          </>
        )}
      </div>
      {error && (
        <p class="hv-modal__error">
          {error}{' '}
          {!client && <button type="button" class="hv-btn hv-btn--sm" onClick={() => setAttempt((n) => n + 1)}>Noch einmal</button>}
        </p>
      )}

      {query && client && (
        catalog === null
          ? <p class="muted">Lade alle Punkte des Reglers für den Filter…</p>
          : hits.length === 0
            ? <p class="muted">Kein OEM-Datenpunkt passt zum Filter.</p>
            : (
              <>
                {totalHits > OEM_FILTER_LIMIT && (
                  <p class="muted">{totalHits} Treffer, die ersten {OEM_FILTER_LIMIT} stehen da und werden gelesen. Filter weiter eingrenzen.</p>
                )}
                {hits.map((v) => row(v, 0, true))}
              </>
            )
      )}

      {!query && groups && groups.length === 0 && <p class="muted">Für diese Ebene keine Punkte.</p>}
      {!query && groups?.map((g) => {
        const isOpen = open === g.gruppe;
        return (
          <div key={g.gruppe} class="hv-node">
            <div class="hv-node__header">
              <button class="hv-node__caret" onClick={() => toggle(g.gruppe)} aria-expanded={isOpen}>{isOpen ? '▾' : '▸'}</button>
              <span class="hv-node__name hv-oem__group" onClick={() => toggle(g.gruppe)}>{stripGroup(g.gruppe, prefix) || 'Sonstiges'}</span>
              <StatusBadge tone="neutral">{g.anzahl}</StatusBadge>
            </div>
            {isOpen && (
              <div class="hv-node__children">
                {points === null ? <p class="muted" style={{ paddingLeft: 10 }}>Lade Punkte…</p> : points.map((v) => row(v, 1, false))}
              </div>
            )}
          </div>
        );
      })}

      {editing && client && (
        <ServiceEditModal plantId={plantId} clientId={client.clientId} deviceId={deviceId}
                          point={editing.point} last={lastOf(editing)}
                          onWritten={(r) => { if (r) setReadings((m) => new Map(m).set(editing.point.address, r)); }}
                          onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

/** Eine Zeile wie LeafRow, nur mit Werten aus dem Fernzugang. */
function OemLeafRow({ view, last, depth, now, reading, group, ownLevel, onEdit }: {
  view: ServicePointView; last: ServiceReading | null; depth: number; now: number; reading: boolean;
  group: string | null; ownLevel: string | null; onEdit(): void;
}) {
  const p = view.point;
  const age = ageOf(last, now);
  const tone = reading ? 'info' : !last ? null : last.quality === 'bad' ? 'error' : age !== null && age < 120 ? 'ok' : 'warn';
  const value = last
    ? (last.quality === 'bad' ? `Fehler${last.error ? `: ${last.error}` : ''}` : displayValue(p, rawValue(last), last.text))
    : '–';
  return (
    <div class="hv-leaf-wrap">
      <div class="hv-leaf hv-oem__leaf" style={{ paddingLeft: `${depth * 10}px` }}>
        <span aria-hidden="true" />
        <span aria-hidden="true" />
        <span class="hv-leaf__marker">◇</span>
        <span class="hv-leaf__label">
          <span class="hv-leaf__name">{p.label || p.address}</span>
          <span class="hv-leaf__meta muted">
            {p.line && <span class="hv-leaf__symbol mono">{p.line}</span>}
            <span class="hv-leaf__type-badge">{p.valueType}</span>
            {group && <span>{group}</span>}
          </span>
        </span>
        <span class="hv-leaf__value hv-oem__value" title={last?.at ? `Gelesen ${new Date(last.at).toLocaleString('de-AT')}` : 'Noch nicht gelesen'}>
          {tone ? <span class={`hv-leaf__dot hv-leaf__dot--${tone}`} /> : <span />}
          <span class="hv-oem__reading">
            <span class={`hv-leaf__reading${last ? '' : ' muted'}`}>{value}</span>
            <span class="hv-oem__age muted">{reading ? 'liest…' : formatAge(age)}</span>
          </span>
        </span>
        <div class="hv-leaf__badges">
          {(p.level === 'oem' || (p.level === 'fachmann' && ownLevel === 'oem')) && (
            <StatusBadge tone="warn" title={`Sichtbar ab Ebene ${levelName(p.level)}`}>{levelName(p.level)}</StatusBadge>
          )}
        </div>
        {p.writable ? (
          <button type="button" class="hv-leaf__send" title="Wert schreiben…" aria-label={`Wert schreiben für ${p.label}`} onClick={onEdit}>✎</button>
        ) : <span />}
      </div>
    </div>
  );
}
